import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, KeyRound, Shield, ShieldCheck, UserX, UserCheck,
  Loader2, X, Mail, Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const inputCls =
  "w-full rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2.5 text-sm text-[#1a202c] focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/15 outline-none transition-all";
const labelCls = "text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold";

function initials(name) {
  return (name || "??").trim().split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function RoleBadge({ role }) {
  const isAdmin = role === "admin";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-bold border ${
        isAdmin
          ? "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]"
          : "bg-[#e0f2fe] text-[#075985] border-[#7dd3fc]"
      }`}
    >
      {isAdmin ? <ShieldCheck className="h-3 w-3" strokeWidth={2.6} /> : <Shield className="h-3 w-3" strokeWidth={2.4} />}
      {role}
    </span>
  );
}

function UserFormDialog({ open, onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    email: initial?.email || "",
    name: initial?.name || "",
    password: "",
    role: initial?.role || "member",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: initial?.email || "",
        name: initial?.name || "",
        password: "",
        role: initial?.role || "member",
      });
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/users/${initial.id}`, { name: form.name.trim(), role: form.role });
        toast.success("User updated");
      } else {
        if (form.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          setSaving(false);
          return;
        }
        await api.post("/users", {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          password: form.password,
          role: form.role,
        });
        toast.success("User created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-[#e2e8f0] rounded-2xl max-w-md" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Edit user" : "Add team member"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Email</label>
            <input
              data-testid="user-form-email"
              type="email"
              required
              autoFocus={!isEdit}
              disabled={isEdit}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={`${inputCls} mt-1.5 ${isEdit ? "bg-[#f7fafc] cursor-not-allowed" : ""}`}
              placeholder="sarah@agency.co"
            />
          </div>
          <div>
            <label className={labelCls}>Full name</label>
            <input
              data-testid="user-form-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`${inputCls} mt-1.5`}
              placeholder="Sarah Jones"
            />
          </div>
          {!isEdit && (
            <div>
              <label className={labelCls}>Initial password</label>
              <input
                data-testid="user-form-password"
                type="text"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={`${inputCls} mt-1.5 font-mono-tight`}
                placeholder="6+ characters — share securely with the user"
              />
              <div className="mt-1 text-[10px] text-[#a0aec0]">
                Share this with the user; they can change it after first login.
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>Role</label>
            <select
              data-testid="user-form-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className={`${inputCls} mt-1.5`}
            >
              <option value="member">Member — view everything, can&apos;t trigger live API runs</option>
              <option value="admin">Admin — full access, can manage users</option>
            </select>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} data-testid="user-form-submit" className="btn-primary">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onClose, user }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setPw(""); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${user.email}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-[#e2e8f0] rounded-2xl max-w-md" data-testid="user-reset-pw-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Reset password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-[#4a5568]">
            Set a new password for <span className="font-semibold text-[#1a202c]">{user?.email}</span>. They&apos;ll be signed in with the new password — make sure to share it securely.
          </div>
          <div>
            <label className={labelCls}>New password</label>
            <input
              data-testid="user-reset-pw-input"
              autoFocus
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className={`${inputCls} mt-1.5 font-mono-tight`}
              placeholder="6+ characters"
            />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} data-testid="user-reset-pw-submit" className="btn-primary">
              {saving ? "Resetting…" : "Reset password"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: me, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required.");
      navigate("/", { replace: true });
    }
  }, [isAdmin, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.role || "").toLowerCase().includes(q),
    );
  }, [users, filter]);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`${u.email} ${u.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update user");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone. Any clients they were assigned to will be unassigned.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete user");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Team</div>
          <h1 className="font-display text-3xl mt-0.5 text-[#1a202c]">Users</h1>
          <p className="text-xs text-[#718096] mt-1">
            Manage who has access to the dashboard. Members can view everything but can&apos;t trigger live API runs.
          </p>
        </div>
        <button
          onClick={() => { setFormInitial(null); setFormOpen(true); }}
          data-testid="users-add-btn"
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="h-3.5 w-3.5" /> Add user
        </button>
      </div>

      <div className="card-soft p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#edf2f7] flex items-center gap-3">
          <Search className="h-3.5 w-3.5 text-[#a0aec0]" />
          <input
            data-testid="users-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email or role…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#cbd5e0]"
          />
          {filter && (
            <button onClick={() => setFilter("")} className="text-[#a0aec0] hover:text-[#1a202c]">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-12 gap-x-4 px-5 py-3 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-xs text-[#a0aec0] flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading users…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-[#a0aec0]">No users match this filter.</div>
        ) : (
          filtered.map((u) => {
            const isMe = u.id === me?.id;
            return (
              <div
                key={u.id}
                className={`grid grid-cols-12 gap-x-4 items-center px-5 py-3 border-b border-[#edf2f7] last:border-b-0 hover:bg-[#f7fafc] text-sm ${
                  !u.is_active ? "opacity-60" : ""
                }`}
                data-testid={`users-row-${u.id}`}
              >
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-xl bg-brand text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {initials(u.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1a202c] truncate">
                      {u.name} {isMe && <span className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold ml-1">(you)</span>}
                    </div>
                    <div className="text-[10px] text-[#a0aec0] font-mono-tight">
                      Joined {new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                </div>
                <div className="col-span-3 text-[#4a5568] truncate flex items-center gap-1.5 min-w-0">
                  <Mail className="h-3 w-3 text-[#cbd5e0] shrink-0" />
                  <span className="truncate" title={u.email}>{u.email}</span>
                </div>
                <div className="col-span-2"><RoleBadge role={u.role} /></div>
                <div className="col-span-1">
                  {u.is_active ? (
                    <span className="text-[10px] text-[#166534] font-semibold uppercase tracking-widest">Active</span>
                  ) : (
                    <span className="text-[10px] text-[#a0aec0] font-semibold uppercase tracking-widest">Off</span>
                  )}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button
                    onClick={() => { setFormInitial(u); setFormOpen(true); }}
                    title="Edit"
                    data-testid={`users-edit-${u.id}`}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-[#4a5568] hover:bg-[#edf2f7] transition-colors"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => { setResetUser(u); setResetOpen(true); }}
                    title="Reset password"
                    data-testid={`users-reset-${u.id}`}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-[#4a5568] hover:bg-[#edf2f7] transition-colors"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => toggleActive(u)}
                    title={u.is_active ? "Deactivate" : "Reactivate"}
                    disabled={isMe}
                    data-testid={`users-toggle-active-${u.id}`}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-[#4a5568] hover:bg-[#edf2f7] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {u.is_active ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => remove(u)}
                    title="Delete"
                    disabled={isMe}
                    data-testid={`users-delete-${u.id}`}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-[#c53030] hover:bg-[#fee2e2] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <UserFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={formInitial} />
      <ResetPasswordDialog open={resetOpen} onClose={() => setResetOpen(false)} user={resetUser} />
    </div>
  );
}
